import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isInFlight, type Transmission } from '@tabservice/tabfile-client';
import { Download, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/toast';
import { FilingStatusPill } from './status-pill';
import { tabfile, errorMessage } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { formatCount, formatDateTimeShort } from '@/lib/format';

export function TransmissionsTable({ filingId, items }: { filingId: string; items: Transmission[] }) {
  return (
    <TableWrap>
      {/* Eight columns is a lot for a card that shares its row with a rail, so
          this table runs tighter than the rest. */}
      <Table className="[&_td]:px-3 [&_th]:px-3">
        <THead>
          <tr>
            <TH align="right">Chunk</TH>
            <TH>Receipt id</TH>
            <TH>Status</TH>
            <TH align="right">Records</TH>
            <TH>Sent</TH>
            <TH>Acknowledged</TH>
            <TH align="right">Attempts</TH>
            <TH>XML</TH>
          </tr>
        </THead>
        <TBody>
          {items.map((item) => (
            <TR key={item.chunk}>
              <TD numeric>{item.chunk}</TD>
              <TD>
                {item.receiptId ? (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap">
                    <span className="font-mono text-sm">{item.receiptId}</span>
                    <CopyButton value={item.receiptId} label="receipt id" />
                  </span>
                ) : (
                  <span className="text-gray-1">Pending</span>
                )}
              </TD>
              <TD>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <FilingStatusPill status={item.status} />
                  {isInFlight(item.status) && item.receiptId ? (
                    <CheckStatusButton filingId={filingId} item={item} />
                  ) : null}
                </div>
              </TD>
              <TD numeric>{formatCount(item.recordCount)}</TD>
              <TD className="whitespace-nowrap text-muted-foreground">{formatDateTimeShort(item.sentAt)}</TD>
              <TD className="whitespace-nowrap text-muted-foreground">{formatDateTimeShort(item.acknowledgedAt)}</TD>
              <TD
                numeric
                className={item.attempts > 1 ? 'text-[color:var(--ts-pill-warning-text)]' : 'text-muted-foreground'}
              >
                {item.attempts}
              </TD>
              <TD>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <XmlButton
                    filingId={filingId}
                    chunk={item.chunk}
                    kind="request"
                    enabled={item.hasRequestXml !== false}
                  />
                  <XmlButton
                    filingId={filingId}
                    chunk={item.chunk}
                    kind="response"
                    enabled={item.hasResponseXml !== false}
                  />
                </div>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {items.some((item) => item.lastError) ? (
        <div className="pt-4">
          {items
            .filter((item) => item.lastError)
            .map((item) => (
              <p key={item.chunk} className="text-base text-destructive">
                Chunk {item.chunk}: {item.lastError}
              </p>
            ))}
        </div>
      ) : null}
    </TableWrap>
  );
}

function XmlButton({
  filingId,
  chunk,
  kind,
  enabled,
}: {
  filingId: string;
  chunk: number;
  kind: 'request' | 'response';
  enabled: boolean;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const label = kind === 'request' ? 'Request' : 'Response';

  const button = (
    <Button
      variant="outline"
      size="sm"
      disabled={!enabled || busy}
      aria-label={`Open the ${kind} XML for chunk ${chunk}`}
      onClick={async () => {
        setBusy(true);
        try {
          // The API returns the document itself, not a link to the archive: Tab Service's own
          // transmitter details are taken out of it on the way, and a link to the stored file
          // would go round that. Opened from memory, so nothing is written to disk unless the
          // person saves it.
          const { data, error, response } = await tabfile.raw.GET(
            '/v1/e-files/{efileId}/transmissions/{chunk}/xml',
            { params: { path: { efileId: filingId, chunk }, query: { kind } }, parseAs: 'text' },
          );
          if (!response.ok || typeof data !== 'string') {
            throw new Error(errorMessage(error) || `The ${kind} XML could not be read.`);
          }
          const url = URL.createObjectURL(new Blob([data], { type: 'application/xml' }));
          window.open(url, '_blank', 'noopener,noreferrer');
          // Long enough for the new tab to have loaded it, short enough not to keep a
          // transmission in memory for the rest of the session.
          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (error) {
          toast.error('Could not open the XML', errorMessage(error));
        } finally {
          setBusy(false);
        }
      }}
    >
      <Download className="h-3.5 w-3.5" aria-hidden="true" />
      {/* The word only fits beside the sidebar on very wide screens; the icon and tooltip carry it otherwise. */}
      <span className="hidden 2xl:inline">{label}</span>
    </Button>
  );

  return (
    <Tooltip content={enabled ? `Open the ${kind} XML` : `No ${kind} XML for this chunk yet`}>
      <span>{button}</span>
    </Tooltip>
  );
}

/** Asks the IRS now instead of waiting for the next scheduled check. */
function CheckStatusButton({ filingId, item }: { filingId: string; item: Transmission }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const next = item.nextPollAt ? formatDateTimeShort(item.nextPollAt) : null;

  return (
    <Tooltip content={next ? `Ask the IRS now. Next automatic check ${next}.` : 'Ask the IRS now.'}>
      <Button
        variant="ghost"
        size="sm"
        disabled={busy}
        aria-label={`Check the IRS status of chunk ${item.chunk} now`}
        onClick={async () => {
          setBusy(true);
          try {
            const result = await tabfile.POST('/v1/e-files/{efileId}/transmissions/{chunk}/check-status', {
              params: { path: { efileId: filingId, chunk: item.chunk } },
            });
            await queryClient.invalidateQueries({ queryKey: queryKeys.filing(filingId) });
            if (isInFlight(result.status)) {
              toast.success('Still processing at the IRS', 'Tab Service keeps checking and updates this page on its own.');
            } else {
              toast.success('The IRS has answered', `Chunk ${item.chunk} is now ${result.status}.`);
            }
          } catch (error) {
            toast.error('Could not check the status', errorMessage(error));
          } finally {
            setBusy(false);
          }
        }}
      >
        <RefreshCw className={busy ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} aria-hidden="true" />
        Check now
      </Button>
    </Tooltip>
  );
}
